import { Skeleton } from "@bednbody/ui/components/skeleton";
import { AdminDataTable, AdminTableIdentity } from "@bednbody/ui/components/admin-data-table";
import { Badge } from "@bednbody/ui/components/badge";
import { Button } from "@bednbody/ui/components/button";
import { Callout } from "@bednbody/ui/components/callout";
import { RetryButton } from "@bednbody/ui/components/retry-button";
import type { ColumnDef } from "@tanstack/react-table";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { NumberInput } from "../settings/settingsFields";
import {
  METRIC_LABEL,
  TIERS,
  TIER_ROLES,
  type Tier,
  type TierBand,
  type TierRole,
  type TierWeight,
  useTierRules,
  useUpdateTierRules,
} from "./rankings.hooks";

/**
 * Tab 2 — the rules that turn a month of work into a rank.
 *
 * These editors were described as living in Settings; they never did. The tables
 * (0244) were Super-Admin-writable with no screen on them, so this is where they
 * first become editable — which is why 0333 added the audit trail first.
 *
 * ⚠ Nothing here is a default Bednbody chose. The seeded 40/30/20/10 weights and
 * the score bands are the approved Tier Rank Systems values (2026-09-21); the
 * rider Silver floor of 71 against the therapist's 77 is deliberate in the source
 * documents, not a typo to be "fixed".
 */

const roleLabel = (role: TierRole) => (role === "therapist" ? "Therapist" : "Rider");
const tierLabel = (tier: string) => tier.charAt(0).toUpperCase() + tier.slice(1);

/* ── weights ────────────────────────────────────────────────────────────── */

interface WeightRow {
  key: string;
  role: TierRole;
  metric: string;
  percent: string;
}

const toWeightRow = (w: TierWeight): WeightRow => ({
  key: `${w.role}/${w.metric}`,
  role: w.role,
  metric: w.metric,
  // numeric(4,3) -> a percentage; round away binary noise (0.3 * 100 = 30.000000000000004).
  percent: String(Math.round(w.weight * 1000) / 10),
});

function WeightsTable({ weights }: { weights: readonly TierWeight[] }) {
  const update = useUpdateTierRules();
  const [draft, setDraft] = useState<WeightRow[]>(() => weights.map(toWeightRow));
  useEffect(() => setDraft(weights.map(toWeightRow)), [weights]);

  const totals = TIER_ROLES.map((role) => ({
    role,
    total: draft
      .filter((r) => r.role === role)
      .reduce((sum, r) => sum + (Number(r.percent) || 0), 0),
  }));

  function save() {
    const parsed = draft.map((r) => ({ ...r, value: Number(r.percent) }));
    if (
      parsed.some(
        (r) => r.percent.trim() === "" || !Number.isFinite(r.value) || r.value < 0 || r.value > 100,
      )
    ) {
      toast.error("Each weight has to be a number from 0 to 100.");
      return;
    }
    update.mutate(
      {
        weights: parsed.map((r) => ({
          role: r.role,
          metric: r.metric,
          weight: Math.round(r.value * 10) / 1000,
        })),
        bands: [],
      },
      {
        onSuccess: () => toast.success("Metric weights saved."),
        onError: (error) => toast.error(error.message),
      },
    );
  }

  const columns = useMemo<ColumnDef<WeightRow, unknown>[]>(
    () => [
      {
        id: "metric",
        accessorFn: (row) => `${row.role} ${row.metric}`,
        header: "Metric",
        cell: ({ row }) => (
          <AdminTableIdentity
            title={METRIC_LABEL[row.original.metric] ?? row.original.metric}
            subtitle={roleLabel(row.original.role)}
            fallback={roleLabel(row.original.role).charAt(0)}
          />
        ),
      },
      {
        id: "weight",
        accessorFn: (row) => Number(row.percent),
        header: "Weight (%)",
        cell: ({ row }) => (
          <NumberInput
            className="w-28"
            step="0.1"
            value={row.original.percent}
            onChange={(value) =>
              setDraft((rows) =>
                rows.map((r) => (r.key === row.original.key ? { ...r, percent: value } : r)),
              )
            }
          />
        ),
      },
    ],
    [],
  );

  return (
    <div className="space-y-8">
      <Callout title="Metric weights">
        How much each measure counts toward a score. Weights are not forced to total 100: a person
        with a measure missing is scored on what exists, and the score is marked partial.
        <div className="mt-2 flex gap-2">
          {totals.map((t) => (
            <Badge
              key={t.role}
              variant={Math.round(t.total * 10) === 1000 ? "success-soft" : "warning-soft"}
            >
              {roleLabel(t.role)} total {Math.round(t.total * 10) / 10}%
            </Badge>
          ))}
        </div>
      </Callout>
      <AdminDataTable
        presentation="standalone"
        data={draft}
        columns={columns}
        getRowId={(row) => row.key}
        searchText={(row) => `${row.role} ${METRIC_LABEL[row.metric] ?? row.metric}`}
        searchPlaceholder="Search metrics…"
        exportFilename="tier-rank-weights"
        exportColumns={[
          { header: "Role", value: (row) => row.role },
          { header: "Metric", value: (row) => row.metric },
          { header: "Weight %", value: (row) => row.percent },
        ]}
        facets={[
          {
            id: "role",
            label: "Role",
            options: TIER_ROLES.map((role) => ({ value: role, label: roleLabel(role) })),
            getValue: (row) => row.role,
          },
        ]}
        header={
          <Button onClick={save} disabled={update.isPending}>
            Save weights
          </Button>
        }
        emptyTitle="No weights"
        emptyDescription="The metric weights could not be found."
      />
    </div>
  );
}

/* ── bands ──────────────────────────────────────────────────────────────── */

interface BandRow {
  key: string;
  role: TierRole;
  tier: Tier;
  /** Editable lower bound, as typed. Bronze starts at 0 and is fixed. */
  from: string;
}

const toBandRows = (bands: readonly TierBand[]): BandRow[] =>
  TIER_ROLES.flatMap((role) =>
    TIERS.map((tier) => {
      const band = bands.find((b) => b.role === role && b.tier === tier);
      return { key: `${role}/${tier}`, role, tier, from: String(band?.min_score ?? 0) };
    }),
  );

const round2 = (n: number) => Math.round(n * 100) / 100;

function BandsTable({ bands }: { bands: readonly TierBand[] }) {
  const update = useUpdateTierRules();
  const [draft, setDraft] = useState<BandRow[]>(() => toBandRows(bands));
  useEffect(() => setDraft(toBandRows(bands)), [bands]);

  /** A tier ends one hundredth below the next tier's floor; Diamond ends at 100. */
  function upperBound(row: BandRow): number | null {
    const next = draft.find(
      (r) => r.role === row.role && r.tier === TIERS[TIERS.indexOf(row.tier) + 1],
    );
    if (!next) return 100;
    const floor = Number(next.from);
    return next.from.trim() === "" || !Number.isFinite(floor) ? null : round2(floor - 0.01);
  }

  function save() {
    const edits: { role: TierRole; tier: Tier; min_score: number; max_score: number }[] = [];
    for (const role of TIER_ROLES) {
      const floors = TIERS.map((tier) => {
        const row = draft.find((r) => r.role === role && r.tier === tier);
        return tier === "bronze" ? 0 : round2(Number(row?.from));
      });
      const ok =
        floors.every((n) => Number.isFinite(n)) &&
        floors.every((n, i) => i === 0 || n > floors[i - 1]!) &&
        floors[3]! <= 100;
      if (!ok) {
        toast.error(
          `${roleLabel(role)}: each rank has to start higher than the one below it, and no higher than 100.`,
        );
        return;
      }
      TIERS.forEach((tier, i) => {
        edits.push({
          role,
          tier,
          min_score: round2(floors[i]!),
          max_score: i === TIERS.length - 1 ? 100 : round2(floors[i + 1]! - 0.01),
        });
      });
    }
    update.mutate(
      { weights: [], bands: edits },
      {
        onSuccess: () => toast.success("Score bands saved."),
        onError: (error) => toast.error(error.message),
      },
    );
  }

  const columns = useMemo<ColumnDef<BandRow, unknown>[]>(
    () => [
      {
        id: "band",
        accessorFn: (row) => `${row.role} ${TIERS.indexOf(row.tier)}`,
        header: "Rank",
        cell: ({ row }) => (
          <AdminTableIdentity
            title={tierLabel(row.original.tier)}
            subtitle={roleLabel(row.original.role)}
            fallback={roleLabel(row.original.role).charAt(0)}
          />
        ),
      },
      {
        id: "from",
        accessorFn: (row) => Number(row.from),
        header: "From score",
        cell: ({ row }) =>
          row.original.tier === "bronze" ? (
            <span className="tabular-nums text-muted-foreground">0</span>
          ) : (
            <NumberInput
              className="w-28"
              step="0.01"
              value={row.original.from}
              onChange={(value) =>
                setDraft((rows) =>
                  rows.map((r) => (r.key === row.original.key ? { ...r, from: value } : r)),
                )
              }
            />
          ),
      },
      {
        id: "to",
        enableSorting: false,
        header: "To score",
        cell: ({ row }) => {
          const to = upperBound(row.original);
          return (
            <span className="tabular-nums text-muted-foreground">
              {to === null ? "—" : to.toFixed(2)}
            </span>
          );
        },
      },
    ],
    // `upperBound` reads `draft`, so the "To" column must rebuild with it.
    [draft],
  );

  return (
    <div className="space-y-8">
      <Callout title="Score bands">
        <p>
          The score each rank starts at. Each rank runs up to just below the next one, so there are
          never gaps.
        </p>
        <p>Bronze always starts at 0 and Diamond always ends at 100.</p>
      </Callout>

      <AdminDataTable
        presentation="standalone"
        data={draft}
        columns={columns}
        getRowId={(row) => row.key}
        searchText={(row) => `${row.role} ${row.tier}`}
        searchPlaceholder="Search ranks…"
        exportFilename="tier-rank-bands"
        exportColumns={[
          { header: "Role", value: (row) => row.role },
          { header: "Rank", value: (row) => row.tier },
          { header: "From score", value: (row) => (row.tier === "bronze" ? 0 : row.from) },
          { header: "To score", value: (row) => upperBound(row) },
        ]}
        facets={[
          {
            id: "role",
            label: "Role",
            options: TIER_ROLES.map((role) => ({ value: role, label: roleLabel(role) })),
            getValue: (row) => row.role,
          },
        ]}
        header={
          <Button onClick={save} disabled={update.isPending}>
            Save bands
          </Button>
        }
        emptyTitle="No score bands"
        emptyDescription="The score bands could not be found."
      />
    </div>
  );
}

export function RulesTab() {
  const rules = useTierRules();

  if (rules.isError) {
    return (
      <div className="space-y-3 p-4">
        <p className="text-sm text-muted-foreground">
          The ranking rules could not be loaded. That is a failed read, not an empty setting.
        </p>
        <RetryButton size="sm" onRetry={() => rules.refetch()} />
      </div>
    );
  }
  if (!rules.data) return <Skeleton className="h-48 motion-reduce:animate-none" />;

  return (
    <div className="space-y-8">
      <WeightsTable weights={rules.data.weights} />
      <BandsTable bands={rules.data.bands} />
    </div>
  );
}
